import { useIntl } from 'react-intl'
import { useMemo } from 'react'

import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import {
  getRewardBundleDisplayPrice,
  getSeatEligiblePassengerCount,
} from 'components/MaldivesDirect/MaldivesDirectSearchPage/utils/bundlePricingDisplay'

export function useBundlePriceLabels() {
  const intl = useIntl()
  return {
    fromLabel: intl.formatMessage({
      id: 'RUJ7Gofk5bJQT5Frz6g8',
      defaultMessage: 'From',
      description: 'Pricing label prefix before fare class price difference in charter flight modal',
    }),
    perPersonSuffix: intl.formatMessage({
      id: 'ew2ARy7ro3tFSM4inpHN',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    }),
    plusLabel: intl.formatMessage({
      id: 'UKiyIYSBCJwNz_25EM7q',
      defaultMessage: '+',
      description: 'Plus sign separating cash and points in Maldives charter reward pricing',
    }),
    loadingLabel: intl.formatMessage({
      id: 'C6m8UbIIgveZbgrQtJ6T',
      defaultMessage: 'Loading...',
      description: 'Loading state text while charter flight prices are being fetched',
    }),
  }
}

export function LoadingPrice({ loadingLabel }: { loadingLabel: string }) {
  return (
    <Caption variant="large" weight="normal" colour="neutral-two">
      {loadingLabel}
    </Caption>
  )
}

export function CashPrice({ fromLabel, price, suffix }: { fromLabel: string; price: number | undefined; suffix: string }) {
  if (!price) return null
  return (
    <Group direction="horizontal" gap={4} verticalAlign="baseline">
      <PriceRowCaption>{fromLabel}</PriceRowCaption>
      <Caption variant="large" weight="bold" colour="neutral-one">
        <FormatCurrency value={price} format="roundedDollar" />
      </Caption>
      <Caption variant="medium" colour="neutral-three" weight="normal">
        {suffix}
      </Caption>
    </Group>
  )
}

export function RewardPrice({
  fromLabel,
  cash,
  points,
  plusLabel,
  suffix,
}: {
  fromLabel: string
  cash: number | undefined
  points: number | undefined
  plusLabel: string
  suffix: string
}) {
  if (points === undefined) return null
  return (
    <Group direction="horizontal" gap={4} verticalAlign="baseline">
      <PriceRowCaption>{fromLabel}</PriceRowCaption>
      {cash !== undefined && cash > 0 && <Caption variant="large" weight="bold" colour="neutral-one">
        <FormatCurrency value={cash} format="roundedDollar" />
      </Caption>}
      {cash !== undefined && cash > 0 && <Caption variant="medium" weight="bold" colour="neutral-one">
        {plusLabel}
      </Caption>}
      <Caption variant="medium" weight="bold" colour="neutral-one">
        <FormatLoyaltyPoints points={points} abbreviated />
      </Caption>
      <Caption variant="medium" colour="neutral-three" weight="normal">
        {suffix}
      </Caption>
    </Group>
  )
}

interface FareClassModalBundlePriceProps {
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FareClassWithReward
  rewardPreviewOnly: boolean
  feederOriginAirportCode?: string
  fetching: boolean
  bundlePrice?: number
  rewardHotelPrice?: number
}

export function FareClassModalBundlePrice({
  checkInDate,
  checkOutDate,
  occupancies,
  fareClass,
  rewardPreviewOnly,
  feederOriginAirportCode,
  fetching,
  bundlePrice,
  rewardHotelPrice,
}: FareClassModalBundlePriceProps) {
  const isReward = isRewardFareClass(fareClass)
  const labels = useBundlePriceLabels()
  const passengerCount = useMemo(() => getSeatEligiblePassengerCount(occupancies), [occupancies])
  const {
    fetching: rewardFetching,
    rewardSeatPointsPerPerson,
    rewardSeatCashCoPayPerPerson,
    rewardAvailable,
  } = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass,
    disabled: !isReward || rewardPreviewOnly,
    feederOriginAirportCode,
  })

  if (fetching || rewardFetching) {
    return <LoadingPrice loadingLabel={labels.loadingLabel} />
  }

  if (isReward) {
    const { points, cash } = getRewardBundleDisplayPrice({
      fareClass,
      rewardPreviewOnly,
      rewardAvailable,
      rewardSeatPointsPerPerson,
      rewardSeatCashCoPayPerPerson,
      rewardHotelPrice,
      passengerCount,
    })
    return <RewardPrice fromLabel={labels.fromLabel} cash={cash} points={points} plusLabel={labels.plusLabel} suffix={labels.perPersonSuffix} />
  }

  return <CashPrice fromLabel={labels.fromLabel} price={bundlePrice ? bundlePrice / passengerCount : undefined} suffix={labels.perPersonSuffix} />
}
